import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FaBriefcase,
  FaBuilding,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaDollarSign,
  FaMapMarkerAlt,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import {
  FiBriefcase,
  FiCheckCircle,
  FiClipboard,
  FiCopy,
  FiDownload,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiFilter,
  FiPlus,
  FiTrash2,
  FiX,
  FiImage,
  FiUpload,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";
import { API_BASE_URL } from "../utils/config";

const API_BASE = API_BASE_URL || "https://ingrainhirebackend.ingrainsystems.com/api";

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function JobPost() {
  const [jobs, setJobs] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");

  // ✅ Backend stats
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeCount: 0,
    draftCount: 0,
    expiredCount: 0,
    uniqueDepartments: [],
    uniqueRoles: [],
    uniqueSalaries: [],
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [salaryFilter, setSalaryFilter] = useState("");

  // Dropdown states
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSalaryDropdown, setShowSalaryDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Dropdown refs
  const roleDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const salaryDropdownRef = useRef(null);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalMessage, setModalMessage] = useState({ type: "", text: "" });

  // ✅ Company Profile Image State (separate from formData for easier handling)
  const [companyProfileFile, setCompanyProfileFile] = useState(null);
  const [companyProfilePreview, setCompanyProfilePreview] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    role: "",
    description: "",
    skills: "",
    experience: "",
    location: "",
    salary: "",
    department: "",
    vacancies: 1,
    assessmentIds: [],
    expiryDate: "",
    companyName: "",
    aboutCompany: "",
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem("jobPost_itemsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });

  const navigate = useNavigate();
  const clientId =
    localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
        setShowDepartmentDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      if (salaryDropdownRef.current && !salaryDropdownRef.current.contains(event.target)) {
        setShowSalaryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchAllData();
  }, [clientId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError("");

      const jobsUrl = clientId
        ? `${API_BASE}/jobs/all/${clientId}`
        : `${API_BASE}/jobs/all`;

      const [jobsRes, quizRes] = await Promise.allSettled([
        axios.get(jobsUrl),
        axios.get(`${API_BASE}/admin/getallquizes`),
      ]);

      if (jobsRes.status === "fulfilled" && jobsRes.value?.data?.success) {
        const fetchedJobs = jobsRes.value.data.jobPosts || [];
        const fetchedStats = jobsRes.value.data.stats || {};

        setJobs(fetchedJobs);
        setStats({
          totalJobs: fetchedStats.totalJobs ?? fetchedJobs.length,
          activeCount: fetchedStats.activeCount ?? 0,
          draftCount: fetchedStats.draftCount ?? 0,
          expiredCount: fetchedStats.expiredCount ?? 0,
          uniqueDepartments: fetchedStats.uniqueDepartments ?? [],
          uniqueRoles: fetchedStats.uniqueRoles ?? [],
          uniqueSalaries: fetchedStats.uniqueSalaries ?? [],
        });

        const uniqueRolesFromBackend = fetchedStats.uniqueRoles || [];
        setRoles(uniqueRolesFromBackend.map((name, idx) => ({ _id: idx, name })));
      } else if (jobsRes.status === "fulfilled" && Array.isArray(jobsRes.value?.data)) {
        setJobs(jobsRes.value.data);
      } else {
        const msg =
          jobsRes.status === "rejected"
            ? jobsRes.reason?.response?.data?.message || jobsRes.reason?.message
            : jobsRes.value?.data?.message || "Failed to load jobs";
        console.warn("Could not load jobs:", msg);
        if (!jobs.length) {
          setError(msg);
        }
      }

      if (quizRes.status === "fulfilled" && quizRes.value?.data?.quizzes) {
        setQuizzes(quizRes.value.data.quizzes);
      }
    } catch (err) {
      console.error("Fetch jobs error:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper: get full image URL
  const getFullImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const base = API_BASE.replace("/api", "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${cleanPath.replace(/\\/g, "/")}`;
  };

  // ✅ Filtered Jobs (client-side)
  const filteredJobs = jobs.filter((job) => {
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      const roleMatch = (job.role || "").toLowerCase().includes(term);
      const skillsMatch = (job.skills || "").toLowerCase().includes(term);
      const locMatch = (job.location || "").toLowerCase().includes(term);
      const deptMatch = (job.department || "").toLowerCase().includes(term);
      const idMatch = (job._id || "").toLowerCase().includes(term);
      if (!roleMatch && !skillsMatch && !locMatch && !deptMatch && !idMatch) return false;
    }

    if (roleFilter && job.role !== roleFilter) return false;
    if (departmentFilter && job.department !== departmentFilter) return false;

    if (statusFilter === "active" && (job.status || "active") !== "active") return false;
    if (statusFilter === "draft" && job.status !== "draft") return false;
    if (statusFilter === "expired") {
      const isExp =
        job.expiryDate && new Date(job.expiryDate) < new Date(new Date().setHours(0, 0, 0, 0));
      if (!isExp) return false;
    }

    if (salaryFilter && job.salary !== salaryFilter) return false;

    if (dateFilter) {
      const jobDate = new Date(job.createdAt || job.appliedAt || job.expiryDate)
        .toISOString()
        .slice(0, 10);
      if (jobDate !== dateFilter) return false;
    }

    return true;
  });

  const hasActiveFilters = Boolean(
    searchQuery || roleFilter || departmentFilter || statusFilter || dateFilter || salaryFilter
  );

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setRoleFilter("");
    setDepartmentFilter("");
    setStatusFilter("");
    setRoleSearchQuery("");
    setDateFilter("");
    setSalaryFilter("");
    setCurrentPage(1);
  };

  const handleCardClick = (type) => {
    if (type === "all") {
      clearFilters();
    } else if (type === "active") {
      setStatusFilter((prev) => (prev === "active" ? "" : "active"));
    } else if (type === "draft") {
      setStatusFilter((prev) => (prev === "draft" ? "" : "draft"));
    } else if (type === "expired") {
      setStatusFilter((prev) => (prev === "expired" ? "" : "expired"));
    }
    setCurrentPage(1);

    const tableSection = document.querySelector(".emp-dash__card:last-child");
    if (tableSection) {
      tableSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredJobs.slice(indexOfFirstItem, indexOfLastItem);

  const handleItemsPerPageChange = (e) => {
    const val = Number(e.target.value);
    setItemsPerPage(val);
    localStorage.setItem("jobPost_itemsPerPage", String(val));
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageClick = (page) => {
    if (typeof page === "number") setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
        pageNumbers.push(i);
      } else if (i === currentPage - 3 || i === currentPage + 3) {
        pageNumbers.push("...");
      }
    }
    return pageNumbers;
  };

  // Form handlers
  const openCreateModal = () => {
    setIsEditing(false);
    setCurrentJobId(null);
    setFormData({
      role: "",
      description: "",
      skills: "",
      experience: "",
      location: "",
      salary: "",
      department: "",
      vacancies: 1,
      assessmentIds: [],
      expiryDate: "",
      companyName: "",
      aboutCompany: "",
    });
    setCompanyProfileFile(null);
    setCompanyProfilePreview("");
    setModalMessage({ type: "", text: "" });
    setIsModalOpen(true);
  };

  const handleEdit = (job) => {
    setIsEditing(true);
    setCurrentJobId(job._id);
    setFormData({
      role: job.role || "",
      description: job.description || "",
      skills: job.skills || "",
      salary: job.salary || "",
      location: job.location || "",
      experience: job.experience || "",
      department: job.department || "",
      vacancies: job.vacancies ?? 1,
      assessmentIds: job.assessmentIds ? job.assessmentIds.map((a) => a._id || a) : [],
      expiryDate:
        job.expiryDate && !isNaN(Date.parse(job.expiryDate))
          ? new Date(job.expiryDate).toISOString().split("T")[0]
          : "",
      companyName: job.companyName || "",
      aboutCompany: job.aboutCompany || "",
    });
    setCompanyProfileFile(null);
    setCompanyProfilePreview(job.companyProfile ? getFullImageUrl(job.companyProfile) : "");
    setModalMessage({ type: "", text: "" });
    setIsModalOpen(true);
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setIsDetailsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job post?")) return;
    try {
      const res = await axios.delete(`${API_BASE}/jobs/${id}`, {
        data: { clientId: clientId || undefined },
      });
      if (res.data?.success || res.status === 200) {
        toast.success("Job post deleted successfully!");
        fetchAllData();
      } else {
        toast.error(res.data?.message || "Failed to delete job.");
      }
    } catch (err) {
      console.error("Failed to delete job:", err);
      toast.error(err.response?.data?.message || "Failed to delete job post.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle company profile image change
  const handleCompanyProfileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file only.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be under 5MB.");
      return;
    }

    setCompanyProfileFile(file);

    // Preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setCompanyProfilePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAssessmentToggle = (assessmentId) => {
    setFormData((prev) => {
      const current = prev.assessmentIds;
      if (current.includes(assessmentId)) {
        return { ...prev, assessmentIds: current.filter((id) => id !== assessmentId) };
      } else {
        return { ...prev, assessmentIds: [...current, assessmentId] };
      }
    });
  };

  // ✅ UPDATED: use FormData for file upload
  const handleSubmit = async (e, forcedStatus = null) => {
    e.preventDefault();
    setSubmitting(true);
    setModalMessage({ type: "", text: "" });

    try {
      const dataToSend = new FormData();

      // ✅ Append all text fields
      Object.keys(formData).forEach((key) => {
        if (key === "assessmentIds") {
          formData.assessmentIds.forEach((id) =>
            dataToSend.append("assessmentIds[]", id)
          );
        } else if (formData[key] !== null && formData[key] !== undefined) {
          dataToSend.append(key, formData[key]);
        }
      });

      // ✅ Append clientId
      if (clientId) {
        dataToSend.append("clientId", clientId);
      }

      // ✅ Append status (draft / active)
      if (forcedStatus) {
        dataToSend.append("status", forcedStatus);
      }

      // ✅ Append company profile image if selected
      if (companyProfileFile) {
        dataToSend.append("companyProfile", companyProfileFile);
      }

      let res;
      if (isEditing) {
        res = await axios.put(`${API_BASE}/jobs/${currentJobId}`, dataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${API_BASE}/jobs/create`, dataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.data?.success || res.status === 200 || res.status === 201) {
        const successText = isEditing
          ? "Job post updated successfully!"
          : "Job post created successfully!";
        toast.success(successText);
        setModalMessage({ type: "success", text: successText });
        fetchAllData();
        setTimeout(() => {
          setIsModalOpen(false);
          setModalMessage({ type: "", text: "" });
          setCompanyProfileFile(null);
          setCompanyProfilePreview("");
        }, 1200);
      } else {
        setModalMessage({
          type: "error",
          text: res.data?.message || "Failed to save job post.",
        });
      }
    } catch (err) {
      console.error("Save job error:", err);
      setModalMessage({
        type: "error",
        text: err.response?.data?.message || err.message || "Failed to save job post.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (job, selectedId = null) => {
    if (typeof job === "string") {
      const fullLink = window.location.origin + job;
      navigator.clipboard.writeText(fullLink);
      const targetId = selectedId || jobs.find((j) => j.link === job)?._id || "";
      if (targetId) {
        setCopiedId(targetId);
        toast.info("Job link copied to clipboard!");
        setTimeout(() => setCopiedId(""), 2200);
      }
      return;
    }

    const fullLink = window.location.origin + (job.link || `/applying-job/${job._id}`);
    const experienceLabel = job.experience
      ? job.experience === "Fresher"
        ? "Fresher"
        : `${job.experience} Years`
      : "Not Specified";
    const jobDesc = job.description || job.responsibilities || "";

    const template = `🚀 Hiring: ${job.role}
🏢 Company: ${job.companyName || "Timely Health / Iyrax"}
📍 Location: ${job.location || "Not Specified"}
💰 Salary / Package: ${job.salary || "Competitive"}
⏳ Experience: ${experienceLabel}

📌 Job Description:
${
  jobDesc
    ? jobDesc
        .split("\n")
        .filter((l) => l.trim())
        .map((l) => `• ${l.trim()}`)
        .join("\n")
    : "• Information available on apply page"
}

🛠 Required Skills:
${
  job.skills
    ? job.skills
        .split(",")
        .filter((s) => s.trim())
        .map((s) => `• ${s.trim()}`)
        .join("\n")
    : "• Relevant skills required"
}

📩 Interested candidates can directly apply here:
${fullLink}`;

    navigator.clipboard.writeText(template);
    setCopiedId(job._id);
    toast.success("Job share message copied to clipboard!");
    setTimeout(() => setCopiedId(""), 2200);
  };

  const downloadCSV = () => {
    if (filteredJobs.length === 0) {
      alert("No data available to download!");
      return;
    }

    const headers = [
      "Job ID",
      "Role",
      "Department",
      "Location",
      "Experience",
      "Salary",
      "Vacancies",
      "Assessments Count",
      "Deadline",
      "Status",
      "Apply Link",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredJobs.map((job) => {
        const fullLink = `${window.location.origin}${job.link || `/applying-job/${job._id}`}`;
        const deadline = job.expiryDate ? formatDate(job.expiryDate) : "Not Set";
        return [
          `"${job._id || ""}"`,
          `"${(job.role || "").replace(/"/g, '""')}"`,
          `"${(job.department || "N/A").replace(/"/g, '""')}"`,
          `"${(job.location || "N/A").replace(/"/g, '""')}"`,
          `"${(job.experience || "Not specified").replace(/"/g, '""')}"`,
          `"${(job.salary || "Competitive").replace(/"/g, '""')}"`,
          job.vacancies ?? 1,
          job.assessmentIds?.length || 0,
          `"${deadline}"`,
          `"${job.status || "active"}"`,
          `"${fullLink}"`,
        ].join(",");
      }),
    ];

    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job_postings_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPeriodLabel = () => {
    try {
      return new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Current Period";
    }
  };

  if (loading) {
    return (
      <div className="emp-dash">
        <div className="emp-dash__loading">
          <div className="emp-dash__spinner" />
          <p className="emp-dash__loading-text">Loading job postings...</p>
        </div>
      </div>
    );
  }

  if (error && jobs.length === 0) {
    return (
      <div className="emp-dash">
        <main className="grid place-items-center min-h-[60vh] p-4">
          <div className="emp-dash__card max-w-[520px] w-full">
            <div className="emp-dash__card-header">
              <div>
                <h3 className="emp-dash__card-title">Couldn't load job postings</h3>
                <p className="emp-dash__card-desc text-red-600 mt-1">{error}</p>
              </div>
              <button type="button" className="emp-dash__card-link" onClick={() => fetchAllData()}>
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="emp-dash">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} />
      <div className="w-full">
        {/* Header */}
        <div className="emp-dash__header mb-4">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="emp-dash__greeting text-lg sm:text-xl font-bold whitespace-nowrap">
              Job <span>Postings</span>
            </h1>
          </div>
          <div className="emp-dash__date-pill">
            <FaCalendarAlt />
            <span>{getPeriodLabel()}</span>
          </div>
        </div>

        {/* ✅ KPI Stats — sab backend se */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div
            className="emp-dash__stat cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => handleCardClick("all")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Jobs</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiBriefcase />
              </div>
            </div>
            <div className="emp-dash__stat-value">{stats.totalJobs}</div>
            <div className="emp-dash__stat-meta">all posted openings</div>
          </div>

          <div
            className="emp-dash__stat cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => handleCardClick("active")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Active Openings</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--present">
                <FiCheckCircle />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-600">{stats.activeCount}</div>
            <div className="emp-dash__stat-meta">published & live</div>
          </div>

          <div
            className="emp-dash__stat cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => handleCardClick("draft")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Draft Posts</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--late">
                <FiEdit2 />
              </div>
            </div>
            <div className="emp-dash__stat-value text-amber-600">{stats.draftCount}</div>
            <div className="emp-dash__stat-meta">unpublished drafts</div>
          </div>

          <div
            className="emp-dash__stat cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => handleCardClick("filtered")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Filtered Records</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiFilter />
              </div>
            </div>
            <div className="emp-dash__stat-value">{filteredJobs.length}</div>
            <div className="emp-dash__stat-meta">matching filters</div>
          </div>

          <div
            className="emp-dash__stat col-span-2 lg:col-span-1 cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={() => handleCardClick("expired")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Expired / Closed</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--absent">
                <FaClock />
              </div>
            </div>
            <div className="emp-dash__stat-value text-rose-600">{stats.expiredCount}</div>
            <div className="emp-dash__stat-meta">passed deadline</div>
          </div>
        </div>

        {/* Filters Card */}
        <div className="emp-dash__card mb-6">
          {/* Desktop Filter Bar */}
          <div className="hidden lg:block">
            <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Search */}
                <div className="relative min-w-[140px] flex-1 max-w-[220px]">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                  <input
                    type="text"
                    placeholder="Search Role or Skills..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FiX className="text-xs" />
                    </button>
                  )}
                </div>

                {/* Department Filter */}
                <div className="relative" ref={departmentDropdownRef}>
                  <button
                    onClick={() => {
                      setShowDepartmentDropdown(!showDepartmentDropdown);
                      setShowRoleDropdown(false);
                      setShowStatusDropdown(false);
                      setShowSalaryDropdown(false);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all bg-white whitespace-nowrap ${
                      departmentFilter
                        ? "border-blue-500 text-blue-700 ring-2 ring-blue-500/10 bg-blue-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaBuilding className="text-gray-400 text-[10px]" />
                    <span className="truncate max-w-[110px]">
                      {departmentFilter || "Departments"}
                    </span>
                    <span className="text-gray-400 text-[10px]">▾</span>
                  </button>
                  {showDepartmentDropdown && (
                    <div
                      className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl min-w-[200px] max-h-60 overflow-y-auto"
                      style={{
                        zIndex: 99999,
                        top: departmentDropdownRef.current
                          ? departmentDropdownRef.current.getBoundingClientRect().bottom + 4
                          : "auto",
                        left: departmentDropdownRef.current
                          ? departmentDropdownRef.current.getBoundingClientRect().left
                          : "auto",
                      }}
                    >
                      <div
                        onClick={() => {
                          setDepartmentFilter("");
                          setShowDepartmentDropdown(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 cursor-pointer hover:bg-blue-50"
                      >
                        All Departments
                      </div>
                      {stats.uniqueDepartments.map((dept) => (
                        <div
                          key={dept}
                          onClick={() => {
                            setDepartmentFilter(dept);
                            setShowDepartmentDropdown(false);
                          }}
                          className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                            departmentFilter === dept
                              ? "bg-blue-50 text-blue-700 font-semibold"
                              : "text-gray-700"
                          }`}
                        >
                          {dept}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Role Filter */}
                <div className="relative" ref={roleDropdownRef}>
                  <button
                    onClick={() => {
                      setShowRoleDropdown(!showRoleDropdown);
                      setShowDepartmentDropdown(false);
                      setShowStatusDropdown(false);
                      setShowSalaryDropdown(false);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all bg-white whitespace-nowrap ${
                      roleFilter
                        ? "border-blue-500 text-blue-700 ring-2 ring-blue-500/10 bg-blue-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaBriefcase className="text-gray-400 text-[10px]" />
                    <span className="truncate max-w-[110px]">{roleFilter || "Roles"}</span>
                    <span className="text-gray-400 text-[10px]">▾</span>
                  </button>
                  {showRoleDropdown && (
                    <div
                      className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl min-w-[210px] max-h-64 overflow-y-auto"
                      style={{
                        zIndex: 99999,
                        top: roleDropdownRef.current
                          ? roleDropdownRef.current.getBoundingClientRect().bottom + 4
                          : "auto",
                        left: roleDropdownRef.current
                          ? roleDropdownRef.current.getBoundingClientRect().left
                          : "auto",
                      }}
                    >
                      <div className="p-2 border-b border-gray-100 bg-gray-50 sticky top-0 z-10">
                        <div className="relative">
                          <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                          <input
                            type="text"
                            placeholder="Filter roles..."
                            value={roleSearchQuery}
                            onChange={(e) => setRoleSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-7 pr-2 py-1 text-xs bg-white border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div
                        onClick={() => {
                          setRoleFilter("");
                          setShowRoleDropdown(false);
                          setRoleSearchQuery("");
                        }}
                        className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 cursor-pointer hover:bg-blue-50"
                      >
                        All Roles
                      </div>
                      {roles
                        .filter((r) => r.name.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                        .map((r) => (
                          <div
                            key={r._id}
                            onClick={() => {
                              setRoleFilter(r.name);
                              setShowRoleDropdown(false);
                              setRoleSearchQuery("");
                            }}
                            className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                              roleFilter === r.name
                                ? "bg-blue-50 text-blue-700 font-semibold"
                                : "text-gray-700"
                            }`}
                          >
                            {r.name}
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Status Filter */}
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    onClick={() => {
                      setShowStatusDropdown(!showStatusDropdown);
                      setShowRoleDropdown(false);
                      setShowDepartmentDropdown(false);
                      setShowSalaryDropdown(false);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all bg-white whitespace-nowrap ${
                      statusFilter
                        ? "border-blue-500 text-blue-700 ring-2 ring-blue-500/10 bg-blue-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="truncate">
                      {statusFilter
                        ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
                        : "Status"}
                    </span>
                    <span className="text-gray-400 text-[10px]">▾</span>
                  </button>
                  {showStatusDropdown && (
                    <div
                      className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl min-w-[160px]"
                      style={{
                        zIndex: 99999,
                        top: statusDropdownRef.current
                          ? statusDropdownRef.current.getBoundingClientRect().bottom + 4
                          : "auto",
                        left: statusDropdownRef.current
                          ? statusDropdownRef.current.getBoundingClientRect().left
                          : "auto",
                      }}
                    >
                      <div
                        onClick={() => {
                          setStatusFilter("");
                          setShowStatusDropdown(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 cursor-pointer hover:bg-blue-50"
                      >
                        All Statuses
                      </div>
                      <div
                        onClick={() => {
                          setStatusFilter("active");
                          setShowStatusDropdown(false);
                        }}
                        className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                          statusFilter === "active"
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-gray-700"
                        }`}
                      >
                        Active
                      </div>
                      <div
                        onClick={() => {
                          setStatusFilter("draft");
                          setShowStatusDropdown(false);
                        }}
                        className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                          statusFilter === "draft"
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-gray-700"
                        }`}
                      >
                        Draft
                      </div>
                      <div
                        onClick={() => {
                          setStatusFilter("expired");
                          setShowStatusDropdown(false);
                        }}
                        className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                          statusFilter === "expired"
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-gray-700"
                        }`}
                      >
                        Expired
                      </div>
                    </div>
                  )}
                </div>

                {/* Salary Filter */}
                <div className="relative" ref={salaryDropdownRef}>
                  <button
                    onClick={() => {
                      setShowSalaryDropdown(!showSalaryDropdown);
                      setShowRoleDropdown(false);
                      setShowDepartmentDropdown(false);
                      setShowStatusDropdown(false);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all bg-white whitespace-nowrap ${
                      salaryFilter
                        ? "border-blue-500 text-blue-700 ring-2 ring-blue-500/10 bg-blue-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaDollarSign className="text-gray-400 text-[10px]" />
                    <span className="truncate max-w-[100px]">{salaryFilter || "Salaries"}</span>
                    <span className="text-gray-400 text-[10px]">▾</span>
                  </button>
                  {showSalaryDropdown && (
                    <div
                      className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl min-w-[180px] max-h-60 overflow-y-auto"
                      style={{
                        zIndex: 99999,
                        top: salaryDropdownRef.current
                          ? salaryDropdownRef.current.getBoundingClientRect().bottom + 4
                          : "auto",
                        left: salaryDropdownRef.current
                          ? salaryDropdownRef.current.getBoundingClientRect().left
                          : "auto",
                      }}
                    >
                      <div
                        onClick={() => {
                          setSalaryFilter("");
                          setShowSalaryDropdown(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 cursor-pointer hover:bg-blue-50"
                      >
                        All Salaries
                      </div>
                      {stats.uniqueSalaries.map((sal, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            setSalaryFilter(sal);
                            setShowSalaryDropdown(false);
                          }}
                          className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                            salaryFilter === sal
                              ? "bg-blue-50 text-blue-700 font-semibold"
                              : "text-gray-700"
                          }`}
                        >
                          {sal}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Date Filter */}
                <div className="relative">
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    placeholder="Created Date"
                    className="w-[125px] h-8 px-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  />
                </div>
              </div>

              {/* Right - Action Buttons */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm whitespace-nowrap"
                  >
                    <FiTrash2 className="w-3 h-3" />
                    Clear
                  </button>
                )}

                <button
                  onClick={downloadCSV}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-md whitespace-nowrap"
                  title="Export to CSV"
                >
                  <FiDownload className="w-3 h-3" />
                  Export
                </button>

                <button
                  onClick={openCreateModal}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-md whitespace-nowrap"
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  Post Job
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Filter Bar */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <FiFilter className="text-blue-600 text-base" />
                <span>Filters &amp; Actions</span>
                {showMobileFilters ? (
                  <FaChevronUp className="text-gray-400" />
                ) : (
                  <FaChevronDown className="text-gray-400" />
                )}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  <strong>{filteredJobs.length}</strong> jobs
                </span>
                <button
                  onClick={openCreateModal}
                  className="p-1.5 bg-blue-600 text-white rounded-lg text-xs"
                >
                  <FiPlus className="text-sm" />
                </button>
              </div>
            </div>

            {showMobileFilters && (
              <div className="mt-2 p-4 bg-white rounded-xl border border-gray-200 space-y-3">
                {/* Search */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Search Jobs
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="text"
                      placeholder="Search Role, Skills, ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                    />
                  </div>
                </div>

                {/* Department */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">All Departments</option>
                    {stats.uniqueDepartments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">All Roles</option>
                    {roles.map((r) => (
                      <option key={r._id} value={r.name}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status & Date */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                    <input
                      type="date"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                    />
                  </div>
                </div>

                {/* Mobile Action Buttons */}
                <div className="pt-3 border-t border-gray-200 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={downloadCSV}
                      className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-sm"
                    >
                      <FiDownload className="w-4 h-4" />
                      Export
                    </button>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                      >
                        <FiTrash2 className="w-4 h-4" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Job Postings Table Card */}
        <div className="emp-dash__card">
          {filteredJobs.length === 0 ? (
            <div className="emp-dash__card-body py-12 text-center text-gray-500">
              <div className="mb-3 text-4xl text-gray-300">📭</div>
              <p className="mb-1 text-sm font-semibold text-gray-800">No job postings found</p>
              <p className="text-xs text-gray-500 mb-5 max-w-xs mx-auto">
                There are no job postings matching the selected search query or filters.
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="emp-dash__table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Position &amp; Role</th>
                      <th style={{ textAlign: "center" }}>Department</th>
                      <th style={{ textAlign: "center" }}>Location</th>
                      <th style={{ textAlign: "center" }}>Experience</th>
                      <th style={{ textAlign: "center" }}>Package</th>
                      <th style={{ textAlign: "center" }}>Vacancies</th>
                      <th style={{ textAlign: "center" }}>Assessments</th>
                      <th style={{ textAlign: "center" }}>Deadline</th>
                      <th style={{ textAlign: "center" }}>Share / Link</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                      <th style={{ textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((job) => {
                      const isExpired =
                        job.expiryDate &&
                        new Date(job.expiryDate) < new Date(new Date().setHours(0, 0, 0, 0));
                      const isNearExpiry =
                        job.expiryDate &&
                        !isExpired &&
                        new Date(job.expiryDate) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

                      return (
                        <tr key={job._id} className="transition-colors hover:bg-slate-50/50">
                          {/* Role & Company */}
                          <td className="px-3 py-3 text-left">
                            <div className="flex items-center gap-2">
                              {/* ✅ Company Profile Image or Initial */}
                              {job.companyProfile ? (
                                <img
                                  src={getFullImageUrl(job.companyProfile)}
                                  alt={job.companyName || job.role}
                                  className="w-8 h-8 rounded-full object-cover border border-slate-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className="flex items-center justify-center w-7 h-7 text-[10px] font-bold bg-gradient-to-br from-indigo-500 to-blue-600 text-white rounded-full shadow-inner flex-shrink-0"
                                style={{ display: job.companyProfile ? "none" : "flex" }}
                              >
                                {job.role ? job.role.charAt(0).toUpperCase() : "J"}
                              </div>
                              <div className="min-w-0">
                                <span
                                  className="font-semibold text-slate-800 text-xs block truncate max-w-[170px]"
                                  title={job.role}
                                >
                                  {job.role}
                                </span>
                                {job.companyName && (
                                  <span className="text-[10px] text-slate-400 font-medium truncate block max-w-[150px]">
                                    {job.companyName}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Department */}
                          <td className="px-3 py-3 text-center text-slate-600 text-[11px] font-medium whitespace-nowrap">
                            {job.department || "General"}
                          </td>

                          {/* Location */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 px-2 py-0.5 bg-slate-50 rounded border border-slate-200">
                              <FaMapMarkerAlt className="text-slate-400 text-[9px]" />
                              {job.location || "Remote"}
                            </span>
                          </td>

                          {/* Experience */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold border text-slate-700 bg-slate-50 border-slate-200">
                              {job.experience
                                ? job.experience === "Fresher"
                                  ? "Fresher"
                                  : `${job.experience} Yrs`
                                : "Any"}
                            </span>
                          </td>

                          {/* Package */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold border text-emerald-700 bg-emerald-50 border-emerald-100">
                              {job.salary || "Competitive"}
                            </span>
                          </td>

                          {/* Vacancies */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <span className="px-2 py-0.5 text-[10px] font-bold text-slate-600 bg-slate-50 rounded border border-slate-100">
                              {job.vacancies ?? 1}
                            </span>
                          </td>

                          {/* Assessments */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {job.assessmentIds && job.assessmentIds.length > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border bg-indigo-50 text-indigo-700 border-indigo-100">
                                <FiClipboard className="w-3 h-3 text-indigo-500" />
                                {job.assessmentIds.length} Linked
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-300 font-medium">-</span>
                            )}
                          </td>

                          {/* Deadline */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {job.expiryDate ? (
                              <div className="flex flex-col items-center justify-center">
                                <span
                                  className={`text-[11px] font-bold ${
                                    isExpired
                                      ? "text-rose-600"
                                      : isNearExpiry
                                      ? "text-amber-600"
                                      : "text-slate-700"
                                  }`}
                                >
                                  {formatDate(job.expiryDate)}
                                </span>
                                {isExpired && (
                                  <span className="text-[9px] font-bold text-rose-500 uppercase tracking-tight">
                                    Expired
                                  </span>
                                )}
                                {isNearExpiry && (
                                  <span className="text-[9px] font-bold text-amber-500 uppercase tracking-tight">
                                    Expiring soon
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 font-medium text-xs">-</span>
                            )}
                          </td>

                          {/* Share */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => copyToClipboard(job)}
                                className={`relative group flex items-center justify-center w-8 h-8 rounded-lg border transition-all shadow-sm ${
                                  copiedId === job._id
                                    ? "bg-emerald-600 text-white border-emerald-600"
                                    : "bg-indigo-50 border-indigo-200 text-indigo-600 hover:bg-indigo-100 hover:border-indigo-300"
                                }`}
                                title="Copy Share Message"
                              >
                                {copiedId === job._id ? (
                                  <FiCheckCircle className="w-3.5 h-3.5" />
                                ) : (
                                  <FiCopy className="w-3.5 h-3.5" />
                                )}
                              </button>
                              {job.link && (
                                <button
                                  onClick={() =>
                                    window.open(`${window.location.origin}${job.link}`, "_blank")
                                  }
                                  className="relative group flex items-center justify-center w-8 h-8 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all shadow-sm"
                                  title="Open Public Link"
                                >
                                  <FiExternalLink className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <span
                              className={`emp-dash__table-status ${
                                (job.status || "active") === "active"
                                  ? "emp-dash__table-status--present"
                                  : job.status === "draft"
                                  ? "emp-dash__table-status--late"
                                  : "emp-dash__table-status--other"
                              }`}
                            >
                              {(job.status || "active") === "active"
                                ? "Active"
                                : job.status === "draft"
                                ? "Draft"
                                : "Closed"}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => handleViewDetails(job)}
                                className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-all shadow-sm"
                                title="View Specifications"
                              >
                                <FiEye className="w-3.5 h-3.5 text-blue-600" />
                              </button>
                              <button
                                onClick={() => handleEdit(job)}
                                className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all shadow-sm"
                                title="Edit Job"
                              >
                                <FiEdit2 className="w-3.5 h-3.5 text-amber-600" />
                              </button>
                              <button
                                onClick={() => handleDelete(job._id)}
                                className="flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 hover:bg-rose-100 hover:border-rose-300 transition-all shadow-sm"
                                title="Delete Job"
                              >
                                <FiTrash2 className="w-3.5 h-3.5 text-rose-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List */}
              <div className="md:hidden divide-y divide-gray-100">
                {currentItems.map((job) => (
                  <div key={job._id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        {/* ✅ Company Profile Image */}
                        {job.companyProfile ? (
                          <img
                            src={getFullImageUrl(job.companyProfile)}
                            alt={job.companyName || job.role}
                            className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                          />
                        ) : null}
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm leading-tight">
                            {job.role}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            {job.department && (
                              <span className="text-[10px] text-blue-600 font-semibold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                {job.department}
                              </span>
                            )}
                            <span className="text-[10px] text-gray-400">
                              {job.vacancies ?? 1} {job.vacancies === 1 ? "opening" : "openings"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span
                        className={`emp-dash__table-status ${
                          (job.status || "active") === "active"
                            ? "emp-dash__table-status--present"
                            : job.status === "draft"
                            ? "emp-dash__table-status--late"
                            : "emp-dash__table-status--other"
                        }`}
                      >
                        {job.status || "active"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                      <div>
                        <span className="text-[10px] text-gray-400 block font-medium">Package</span>
                        <span className="font-bold text-emerald-700 text-[11px]">
                          {job.salary || "Competitive"}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400 block font-medium">Deadline</span>
                        <span className="font-semibold text-gray-700 text-[11px]">
                          {job.expiryDate ? formatDate(job.expiryDate) : "None"}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400 block font-medium">Location</span>
                        <span className="text-gray-700 truncate block text-[11px]">
                          {job.location || "Remote"}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400 block font-medium">
                          Assessments
                        </span>
                        <span className="text-indigo-600 font-semibold text-[11px]">
                          {job.assessmentIds?.length || 0} Linked
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-1 pt-1">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleViewDetails(job)}
                          className="px-2.5 py-1 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEdit(job)}
                          className="px-2.5 py-1 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-lg"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => copyToClipboard(job)}
                          className="px-2.5 py-1 text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg"
                        >
                          {copiedId === job._id ? "Copied" : "Share"}
                        </button>
                      </div>
                      <button
                        onClick={() => handleDelete(job._id)}
                        className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg"
                      >
                        <FiTrash2 className="text-sm" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-200/50 bg-gray-50/30">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="p-1 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="text-xs text-gray-500 font-medium">
                    Showing{" "}
                    <strong className="text-gray-800">
                      {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredJobs.length)}
                    </strong>{" "}
                    of <strong className="text-gray-800">{filteredJobs.length}</strong> records
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`px-2.5 py-1 text-xs font-semibold border rounded-lg transition-all ${
                      currentPage === 1
                        ? "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
                        : "text-gray-700 bg-white border-gray-300 shadow-sm"
                    }`}
                  >
                    Prev
                  </button>

                  {getPageNumbers().map((page, index) => (
                    <button
                      key={index}
                      onClick={() => (typeof page === "number" ? handlePageClick(page) : null)}
                      disabled={page === "..."}
                      className={`px-3 py-1 text-xs font-semibold border rounded-lg transition-all min-w-[32px] ${
                        page === "..."
                          ? "text-gray-400 bg-transparent border-transparent cursor-default"
                          : currentPage === page
                          ? "text-white bg-blue-600 border-blue-600 shadow-sm"
                          : "text-gray-700 bg-white border-gray-300"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`px-2.5 py-1 text-xs font-semibold border rounded-lg transition-all ${
                      currentPage === totalPages
                        ? "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
                        : "text-gray-700 bg-white border-gray-300 shadow-sm"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md animate-fade-in p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-red-50 hover:text-red-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:rotate-90 border border-gray-200/50"
            >
              <FaTimes className="text-gray-700 hover:text-red-500 transition-colors text-xl font-bold" />
            </button>

            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 pr-16">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">{isEditing ? "✏️" : "💼"}</span>
                  {isEditing ? "Update Job Opening" : "Post New Job Opening"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isEditing
                    ? "Update position parameters, requirements, and assessment links"
                    : "Configure role specifications and assign evaluation quizzes"}
                </p>
              </div>
            </div>

            <div className="p-5 max-h-[calc(90vh-130px)] overflow-y-auto space-y-4">
              {modalMessage.text && (
                <div
                  className={`p-3 rounded-xl flex items-center gap-2.5 text-xs font-semibold ${
                    modalMessage.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}
                >
                  {modalMessage.type === "success" ? (
                    <FiCheckCircle className="text-base" />
                  ) : (
                    <FiX className="text-base" />
                  )}
                  <span>{modalMessage.text}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Job Role / Title *
                    </label>
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Senior Full Stack Engineer"
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Salary / Package *
                    </label>
                    <input
                      type="text"
                      name="salary"
                      value={formData.salary}
                      onChange={handleChange}
                      required
                      placeholder="e.g. ₹8,00,000 - ₹12,00,000 PA"
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Experience *
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    >
                      <option value="">Select Experience</option>
                      <option value="Fresher">Fresher</option>
                      <option value="0-1">0-1 Years</option>
                      <option value="1-2">1-2 Years</option>
                      <option value="2-3">2-3 Years</option>
                      <option value="3-5">3-5 Years</option>
                      <option value="5+">5+ Years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Hyderabad / Remote"
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      placeholder="e.g. Engineering"
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Vacancies *
                    </label>
                    <input
                      type="number"
                      name="vacancies"
                      value={formData.vacancies}
                      onChange={handleChange}
                      min="1"
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Application Deadline
                    </label>
                    <input
                      type="date"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      min={getTodayDateString()}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Required Skills (comma separated) *
                  </label>
                  <input
                    type="text"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    required
                    placeholder="e.g. React, Node.js, TypeScript, PostgreSQL"
                    className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Evaluation Quizzes &amp; Assessments
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto p-2.5 rounded-xl border border-gray-200 bg-gray-50/50">
                    {quizzes.map((quiz) => {
                      const isSelected = formData.assessmentIds.includes(quiz._id);
                      return (
                        <label
                          key={quiz._id}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm font-semibold"
                              : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="rounded text-blue-600 focus:ring-0"
                            checked={isSelected}
                            onChange={() => handleAssessmentToggle(quiz._id)}
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-medium truncate">
                              {quiz.topic || quiz.title}
                            </span>
                            <span className="text-[9px] text-gray-400">
                              {quiz.role || "General"} • {quiz.experienceLevel || "All levels"}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                    {quizzes.length === 0 && (
                      <div className="col-span-2 text-center py-4 text-xs text-gray-400">
                        No assessments available to link.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Job Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="3"
                    placeholder="Outline key responsibilities, qualifications, and role summary..."
                    className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="e.g. Timely Health"
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      About Company
                    </label>
                    <input
                      type="text"
                      name="aboutCompany"
                      value={formData.aboutCompany}
                      onChange={handleChange}
                      placeholder="Brief company intro..."
                      className="w-full px-3.5 py-2 text-xs border border-gray-300 rounded-xl bg-white"
                    />
                  </div>
                </div>

                {/* ✅ Company Profile Image Upload */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Company Profile / Logo
                  </label>

                  <div className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
                    {/* Preview */}
                    <div className="w-16 h-16 rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                      {companyProfilePreview ? (
                        <img
                          src={companyProfilePreview}
                          alt="Company Profile Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FiImage className="text-gray-300 text-2xl" />
                      )}
                    </div>

                    {/* Upload button */}
                    <div className="flex-1 min-w-0">
                      <input
                        type="file"
                        id="companyProfileInput"
                        accept="image/*"
                        onChange={handleCompanyProfileChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="companyProfileInput"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 cursor-pointer transition-all"
                      >
                        <FiUpload className="w-3.5 h-3.5" />
                        {companyProfilePreview ? "Change Image" : "Upload Image"}
                      </label>
                      <p className="text-[10px] text-gray-400 mt-1.5">
                        PNG, JPG, JPEG • Max 5MB
                      </p>
                      {companyProfileFile && (
                        <p className="text-[10px] text-emerald-600 font-semibold mt-1 truncate">
                          ✅ {companyProfileFile.name}
                        </p>
                      )}
                    </div>

                    {/* Remove button */}
                    {companyProfilePreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setCompanyProfileFile(null);
                          setCompanyProfilePreview("");
                        }}
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 hover:bg-rose-100 transition-all flex-shrink-0"
                        title="Remove Image"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, "draft")}
                    disabled={submitting}
                    className="px-4 py-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-xl transition-all"
                  >
                    Save Draft
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-md active:scale-95 disabled:opacity-50"
                  >
                    {submitting
                      ? "Saving..."
                      : isEditing
                      ? "Update & Publish"
                      : "Publish Position"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* VIEW DETAILS MODAL */}
      {isDetailsModalOpen && selectedJob && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md animate-fade-in p-4"
          onClick={() => setIsDetailsModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsDetailsModalOpen(false)}
              className="absolute top-3 right-3 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-red-50 hover:text-red-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:rotate-90 border border-gray-200/50"
            >
              <FaTimes className="text-gray-700 hover:text-red-500 transition-colors text-xl font-bold" />
            </button>

            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 pr-16">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  {selectedJob.role}
                </h3>
              </div>
            </div>

            <div className="p-5 max-h-[calc(90vh-130px)] overflow-y-auto space-y-4 text-xs">
              {/* ✅ Company Profile Image */}
              {selectedJob.companyProfile && (
                <div className="flex items-center gap-3 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                  <img
                    src={getFullImageUrl(selectedJob.companyProfile)}
                    alt={selectedJob.companyName || selectedJob.role}
                    className="w-16 h-16 rounded-xl object-cover border border-indigo-200"
                  />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Company Profile
                    </p>
                    <p className="text-sm font-bold text-gray-800 mt-0.5">
                      {selectedJob.companyName || "—"}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                <div>
                  <span className="text-gray-400 block font-medium">Salary Package</span>
                  <span className="text-emerald-700 font-bold text-sm">
                    {selectedJob.salary || "Competitive"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block font-medium">Experience</span>
                  <span className="text-gray-800 font-bold">
                    {selectedJob.experience || "Fresher"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block font-medium">Location</span>
                  <span className="text-gray-800 font-medium">
                    {selectedJob.location || "Remote"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block font-medium">Vacancies</span>
                  <span className="text-gray-800 font-medium">{selectedJob.vacancies ?? 1}</span>
                </div>
                <div>
                  <span className="text-gray-400 block font-medium">Application Deadline</span>
                  <span className="text-gray-800 font-medium">
                    {selectedJob.expiryDate ? formatDate(selectedJob.expiryDate) : "None"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block font-medium">Status</span>
                  <span className="font-bold text-blue-600 uppercase tracking-wider">
                    {selectedJob.status || "active"}
                  </span>
                </div>
              </div>

              <div>
                <span className="font-semibold text-gray-700 block mb-1.5">Required Skills:</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedJob.skills ? (
                    selectedJob.skills.split(",").map((s, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg font-medium"
                      >
                        {s.trim()}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">No specific skills listed</span>
                  )}
                </div>
              </div>

              <div>
                <span className="font-semibold text-gray-700 block mb-1.5">
                  Linked Assessments ({selectedJob.assessmentIds?.length || 0}):
                </span>
                <div className="space-y-1.5">
                  {selectedJob.assessmentIds && selectedJob.assessmentIds.length > 0 ? (
                    selectedJob.assessmentIds.map((a, idx) => (
                      <div
                        key={idx}
                        className="p-2.5 bg-indigo-50/50 rounded-lg border border-indigo-100 text-indigo-900 font-medium"
                      >
                        {a.topic || a.title || "Assessment Quiz"}
                      </div>
                    ))
                  ) : (
                    <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-gray-400">
                      No assessments attached to this opening.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <span className="font-semibold text-gray-700 block mb-1.5">Job Description:</span>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-gray-700 whitespace-pre-line leading-relaxed">
                  {selectedJob.description || "No description provided."}
                </div>
              </div>

              <div>
                <span className="font-semibold text-gray-700 block mb-1.5">Direct Apply URL:</span>
                <div className="flex items-center justify-between gap-2 p-2.5 bg-gray-50 rounded-xl border border-gray-200">
                  <span className="text-gray-600 truncate font-mono text-[11px]">
                    {window.location.origin +
                      (selectedJob.link || `/applying-job/${selectedJob._id}`)}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        selectedJob.link || `/applying-job/${selectedJob._id}`,
                        selectedJob._id
                      )
                    }
                    className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 shadow-sm whitespace-nowrap"
                  >
                    Copy URL
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-2.5">
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    handleEdit(selectedJob);
                  }}
                  className="px-4 py-2 font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-all"
                >
                  Edit Job
                </button>
                <button
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="px-5 py-2 font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scale-up {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(15px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.25s ease-out;
        }
        .animate-scale-up {
          animation: scale-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}